/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { FetchData } from 'src/core/api';
import {
  setPagination as setPaginationAction,
  getList,
} from 'src/core/redux/actions';
import { pagination as pageConstants, state as stateConstants } from 'src/constants';
import {
  selectListFetchData,
  selectListData,
  selectPagination
} from 'src/core/redux/selectors';
import { Product } from 'src/models';
import { dateToDisplayFormat } from 'src/utils';

import { deleteProducts as deleteProductsAction } from './actions';
import ProductsTableComponent from './ProductsTableComponent';
import getTableConfig from './config';

const prepareProducts = (products) => products.map((product) => ({
  checkbox: {
    itemId: product.id,
  },
  nameDescription: {
    name: product.name,
    description: product.description,
    detailsLink: product.detailsLink,
  },
  product: product.type,
  status: product.status,
  lastModified: dateToDisplayFormat(product.lastModified),
  actions: {
    canExport: product.canExport,
    detailsLink: product.detailsLink,
  }
}));

const ProductsTableContainer = ({
  // products
  products,
  totalProducts,
  category,
  subcategory,
  isViewAllActive,
  deleteProducts,

  // fetch data props
  totalItems,
  fetchProducts,
  fetchAllProducts,
  fetchDataProps,

  // pagination
  pagination,
  setPagination,
}) => {
  const [selectedProductsIds, setSelectedProductsIds] = React.useState([]);
  const onPaginationChange = (latestPagination) => {
    fetchProducts({
      pagination: { ...latestPagination },
      customProductCategory: category.categoryNr,
      productCategoryFormat: subcategory.subcategoryNr
    });
  };

  useEffect(() => {
    if (!isViewAllActive) {
      setPagination(pageConstants.INITIAL_PAGINATON);

      fetchProducts({
        pagination: pageConstants.INITIAL_PAGINATON,
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr
      });
      setSelectedProductsIds([]);
    }
  }, [
    fetchProducts,
    category.name,
    subcategory.name,
  ]);

  useEffect(() => {
    if (!isViewAllActive) {
      fetchAllProducts({
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr
      });
    }
  }, [category.name, subcategory.name]);

  const tableConfigProps = {
    addProductId: (id) => setSelectedProductsIds([...selectedProductsIds, id]),
    removeProductId: (id) => setSelectedProductsIds(
      selectedProductsIds.filter((itemId) => itemId !== id)
    ),
    setSelectedItems: setSelectedProductsIds,
    productsIds: totalProducts.map((product) => product.id),
    selectedProductsIds,
    isViewAllActive
  };

  const resetData = useCallback(() => setSelectedProductsIds([]), []);

  const onDelete = () => {
    const newPaginationConfig = { ...pagination, currentPage: pageConstants.INITIAL_PAGE };

    setPagination(newPaginationConfig);

    deleteProducts(
      selectedProductsIds,
      isViewAllActive,
      {
        pagination: newPaginationConfig,
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr
      },
      setSelectedProductsIds
    );
  };

  return (
    <FetchData
      {...{
        ...fetchDataProps,
        resetData,
      }}
    >
      <ProductsTableComponent
        {...{
          id: `${subcategory ? `${category.name}-${subcategory.name}` : category.name}`,
          data: prepareProducts(products),
          config: getTableConfig(tableConfigProps),
          totalItems,
          onPaginationChange,
          setPagination,
          pagination,
          selectedProductsIds,
          category: category.name,
          subcategory: subcategory.name,
          onDelete,
        }}
      />
    </FetchData>
  );
};

ProductsTableContainer.propTypes = {
  products: PropTypes.array.isRequired,
  totalProducts: PropTypes.array.isRequired,
  setPagination: PropTypes.func.isRequired,
  pagination: PropTypes.object,
  filters: PropTypes.object,
  sortBy: PropTypes.object,
  fetchProducts: PropTypes.func.isRequired,
  fetchAllProducts: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setSortBy: PropTypes.func.isRequired,
  fetchDataProps: PropTypes.object.isRequired,
  totalItems: PropTypes.number.isRequired,
  category: PropTypes.object.isRequired,
  subcategory: PropTypes.object.isRequired,
  isViewAllActive: PropTypes.bool,
  deleteProducts: PropTypes.bool.isRequired,
};

const makeMapStateToProps = () => {
  const selectProducts = selectListData(Product);
  const selectAllProducts = selectListData(Product, stateConstants.IDENTIFIER_TYPE_TOTAL_LIST);

  const mapStateToProps = (state, ownProps) => {
    const { list, totalItems } = selectProducts(state);
    const { list: totalProducts } = selectAllProducts(state);
    const predefinedProducts = ownProps.products;
    const pagination = ownProps.isViewAllActive ? null :
      selectPagination(Product, pageConstants.INITIAL_PAGINATON)(state);

    return {
      products: predefinedProducts || list,
      totalProducts,
      totalItems,
      fetchDataProps: predefinedProducts ? {
        isFetching: false,
        fetched: true
      } :
        selectListFetchData(Product)(state),
      pagination
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = {
  fetchProducts: getList(Product),
  fetchAllProducts: getList(Product, stateConstants.IDENTIFIER_TYPE_TOTAL_LIST),
  setPagination: setPaginationAction(Product),
  deleteProducts: deleteProductsAction,
};

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(ProductsTableContainer);
